import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import tournamentSocket from './tournamentSocket';
import longTournamentSocket from './longTournamentSocket';
import './SpinWheelOverlay.css';
import { useSound } from './sfx/SoundProvider';
/**
 * Стрелка по-прежнему смотрит влево (9 часов).
 * Серверные углы можно не менять.
 */
const POINTER_DEG = 270;
const CLIENT_ANGLE_CORRECTION = 5;
const WHEEL_SPIN_DURATION_MS = 18000;
const WHEEL_HIDE_DELAY_MS = WHEEL_SPIN_DURATION_MS + 3000; 
// +2 секунды сверху, чтобы приз успел показаться

/**
 * Режим разработки — чтобы колесо не исчезало через 12 секунд.
 * Пока играешься с дизайном, держи true.
 * На проде поставь false.
 */
const DEBUG_WHEEL_ALWAYS_VISIBLE = false;

/* Палитра под неоновый стиль */
const neonPalette = [
  '#141827',
  '#1c2140',
  '#151b32',
  '#202746',
  '#15192c',
  '#1e2341',
  '#151b32',
  '#202746',
  '#141827',
  '#1c2140',
  '#151b32',
  '#202746',
];

const paletteByBuyIn = new Proxy(
  {},
  {
    get: () => neonPalette,
  }
);

/* Конфигурация секторов по бай-ину */
const sectorSpecsByBuyIn = {
  1: [
    { prize: 2, mid: 225 },
    { prize: 3, mid: 105 },
    { prize: 4, mid: 345 },
    { prize: 5, mid: 45 },
    { prize: 8, mid: 285 },
    { prize: 10, mid: 195 },
    { prize: 12, mid: 165 },
    { prize: 15, mid: 360 },
    { prize: 20, mid: 315 },
    { prize: 30, mid: 135 },
    { prize: 50, mid: 75 },
    { prize: 100, mid: 255 },
  ],
  2: [
    { prize: 4, mid: 15 },
    { prize: 6, mid: 45 },
    { prize: 8, mid: 75 },
    { prize: 10, mid: 105 },
    { prize: 16, mid: 135 },
    { prize: 20, mid: 165 },
    { prize: 24, mid: 195 },
    { prize: 30, mid: 225 },
    { prize: 40, mid: 255 },
    { prize: 60, mid: 285 },
    { prize: 100, mid: 315 },
    { prize: 200, mid: 345 },
  ],
  5: [
    { prize: 10, mid: 15 },
    { prize: 15, mid: 45 },
    { prize: 20, mid: 75 },
    { prize: 25, mid: 105 },
    { prize: 30, mid: 135 },
    { prize: 50, mid: 165 },
    { prize: 80, mid: 195 },
    { prize: 100, mid: 225 },
    { prize: 150, mid: 255 },
    { prize: 200, mid: 285 },
    { prize: 300, mid: 315 },
    { prize: 500, mid: 345 },
  ],

  10: [
    { prize: 20, mid: 15 },
    { prize: 25, mid: 45 },
    { prize: 30, mid: 75 },
    { prize: 50, mid: 105 },
    { prize: 80, mid: 135 },
    { prize: 150, mid: 165 },
    { prize: 300, mid: 195 },
    { prize: 500, mid: 225 },
    { prize: 600, mid: 255 },
    { prize: 700, mid: 285 },
    { prize: 800, mid: 315 },
    { prize: 1000, mid: 345 },
  ],

  20: [
    { prize: 40, mid: 15 },
    { prize: 50, mid: 45 },
    { prize: 60, mid: 75 },
    { prize: 80, mid: 105 },
    { prize: 100, mid: 135 },
    { prize: 140, mid: 165 },
    { prize: 200, mid: 195 },
    { prize: 300, mid: 225 },
    { prize: 500, mid: 255 },
    { prize: 1000, mid: 285 },
    { prize: 1500, mid: 315 },
    { prize: 2000, mid: 345 },
  ],
  30: [
    { prize: 60, mid: 15 },
    { prize: 70, mid: 45 },
    { prize: 80, mid: 75 },
    { prize: 90, mid: 105 },
    { prize: 150, mid: 135 },
    { prize: 220, mid: 165 },
    { prize: 250, mid: 195 },
    { prize: 500, mid: 225 },
    { prize: 800, mid: 255 },
    { prize: 1000, mid: 285 },
    { prize: 1500, mid: 315 },
    { prize: 3000, mid: 345 },
  ],
  50: [
    { prize: 100, mid: 105 },
    { prize: 150, mid: 305 },
    { prize: 200, mid: 225 },
    { prize: 250, mid: 75 },
    { prize: 350, mid: 255 },
    { prize: 600, mid: 165 },
    { prize: 1000, mid: 15 },
    { prize: 1500, mid: 285 },
    { prize: 2000, mid: 45 },
    { prize: 3000, mid: 335 },
    { prize: 3500, mid: 195 },
    { prize: 5000, mid: 135 },
  ],
};

/* ── геометрия ───────────────────────────────────────── */

const normAngle = (a) => {
  let x = a % 360;
  if (x < 0) x += 360;
  return x;
};

const degToRadForSvg = (deg0Up) => ((deg0Up - 90) * Math.PI) / 180;

const polar = (r, aDeg) => {
  const a = degToRadForSvg(aDeg);
  return [r * Math.cos(a), r * Math.sin(a)];
};

const cwDist = (a, b) => {
  const d = normAngle(b) - normAngle(a);
  return d < 0 ? d + 360 : d;
};

/* Формат: $1,000 */
const formatPrize = (v) =>
  `$${new Intl.NumberFormat('en-US').format(Number(v) || 0)}`;

/* Клинья по центрам */
function buildSectors(midSpecs) {
  const s = [...midSpecs].sort((x, y) => x.mid - y.mid);
  const n = s.length;
  const out = [];
  for (let i = 0; i < n; i++) {
    const prev = s[(i - 1 + n) % n].mid;
    const cur = s[i].mid;
    const next = s[(i + 1) % n].mid;
    const start = normAngle(cur - cwDist(prev, cur) / 2);
    const end = normAngle(cur + cwDist(cur, next) / 2);
    out.push({
      prize: s[i].prize,
      mid: normAngle(cur),
      start,
      end,
      sweep: cwDist(start, end) || 360,
    });
  }
  return out.sort((a, b) => a.start - b.start);
}

/* Путь кольцевого сектора */
function donutPath(R, r, a1, a2) {
  const largeArc = cwDist(a1, a2) > 180 ? 1 : 0;
  const [x1, y1] = polar(R, a1);
  const [x2, y2] = polar(R, a2);
  const [x3, y3] = polar(r, a2);
  const [x4, y4] = polar(r, a1);
  return [
    `M ${x1.toFixed(3)} ${y1.toFixed(3)}`,
    `A ${R} ${R} 0 ${largeArc} 1 ${x2.toFixed(3)} ${y2.toFixed(3)}`,
    `L ${x3.toFixed(3)} ${y3.toFixed(3)}`,
    `A ${r} ${r} 0 ${largeArc} 0 ${x4.toFixed(3)} ${y4.toFixed(3)}`,
    'Z',
  ].join(' ');
}

/* попадает ли угол в сектор */
const inSector = (angle, start, end, eps = 0.5) =>
  cwDist(start, angle) <= cwDist(start, end) + eps;

/* Нормализуем buy-in из строки/числа */
const parseBuyIn = (v) => {
  const m = String(v ?? '').match(/\d+(\.\d+)?/);
  return m ? Number(m[0]) : 1;
};

export default function SpinWheelOverlay() {
  const { t: translate } = useTranslation();
  const [show, setShow] = useState(false);
  const [angle, setAngle] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [finalPrize, setFinalPrize] = useState(null);
  const [currentBuyIn, setCurrentBuyIn] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const hideTimerRef = useRef(null);
  const plannedPrizeRef = useRef(null);
  const { play } = useSound();
  /* геометрия секторов для текущего бай-ина */
  const sectors = useMemo(() => {
    const bi = parseBuyIn(currentBuyIn);
    const mids = sectorSpecsByBuyIn[bi] || sectorSpecsByBuyIn[1];
    if (!sectorSpecsByBuyIn[bi]) {
      console.warn(
        '[SpinWheel] нет набора секторов для buyIn =',
        currentBuyIn,
        '→ используем $1'
      );
    }
    return buildSectors(mids);
  }, [currentBuyIn]);

  const palette = useMemo(() => {
    const p = paletteByBuyIn[parseBuyIn(currentBuyIn)] || neonPalette;
    return (idx) => p[idx % p.length];
  }, [currentBuyIn]);

  const computeSelectedIndex = useCallback(
    (rot) => {
      const pointerInWheel = normAngle(POINTER_DEG - normAngle(rot));
      return sectors.findIndex((s) =>
        inSector(pointerInWheel, s.start, s.end)
      );
    },
    [sectors]
  );

  /* подписываемся на событие spinWheel */
  useEffect(() => {
    function handleSpinWheel({ angle, prize, buyIn }) {
      setShow(true);
      setAngle(0);
      setSpinning(false);
      setFinalPrize(null);
      setSelectedIndex(-1);
      setCurrentBuyIn(parseBuyIn(buyIn));

      // setTimeout(() => {
      //   setAngle((Number(angle) || 0) + CLIENT_ANGLE_CORRECTION);
      //   setSpinning(true);
      // }, 100);

          setTimeout(() => {
      // 🔊 звук запуска колеса (после того как все зарегистрировались
      // и сервер прислал spinWheel)
      play('wheelFortune').catch(() => {});

      setAngle((Number(angle) || 0) + CLIENT_ANGLE_CORRECTION);
      setSpinning(true);
    }, 100);


      if (!DEBUG_WHEEL_ALWAYS_VISIBLE) {
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
        }
        // hideTimerRef.current = setTimeout(() => setShow(false), 12000);
        hideTimerRef.current = setTimeout(
  () => setShow(false),
  WHEEL_HIDE_DELAY_MS
);
      }

      plannedPrizeRef.current = prize;
    }

    tournamentSocket.on('spinWheel', handleSpinWheel);
    longTournamentSocket?.on?.('spinWheel', handleSpinWheel);

    return () => {
      tournamentSocket.off('spinWheel', handleSpinWheel);
      longTournamentSocket?.off?.('spinWheel', handleSpinWheel);
    };
  }, []);

  /* очистка состояния при скрытии */
  useEffect(() => {
    if (!show && !DEBUG_WHEEL_ALWAYS_VISIBLE) {
      setAngle(0);
      setSpinning(false);
      setFinalPrize(null);
      setSelectedIndex(-1);
      plannedPrizeRef.current = null;
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    }
  }, [show]);

  /* если не шоу и не дебаг — не рендерим вовсе */
  if (!show && !DEBUG_WHEEL_ALWAYS_VISIBLE) return null;

  const buyInPretty = `$${parseBuyIn(currentBuyIn)}`;

  return (
    <div className="wheel-overlay wheel-overlay--neon">
      <div className="wheel-card wheel-card--neon">
        <div className="wheel-card__top">
          <div className="wheel-card__title-block">
            <div className="wheel-title-main">
              {translate('jackpotWheel.title')}
            </div>
            <div className="wheel-title-sub">
              {translate('jackpotWheel.subtitle')}
            </div>
          </div>

          <div className="wheel-buyin-pill">
            <span className="wheel-buyin-label">
              {translate('jackpotWheel.buyIn')}
            </span>
            <span className="wheel-buyin-value">{buyInPretty}</span>
          </div>
        </div>

        <div className="wheel-container wheel-container--neon">
          {/* внешний ореол / глоу */}
          <div className="outer-glow" aria-hidden />
          {/* обод */}
          <div className="outer-ring outer-ring--neon" aria-hidden />
          {/* стрелка слева */}
          <div className="wheel-pointer wheel-pointer--neon" aria-label={translate('jackpotWheel.pointerAria')}>
            <div className="pointer-core" />
          </div>

          <div
            className="wheel wheel--neon"
            // style={{
            //   transform: `rotate(${angle}deg)`,
            //   transition: spinning
            //     ? 'transform 5.4s cubic-bezier(.12,.9,.2,1)'
            //     : 'none',
            // }}
             style={{
              transform: `rotate(${angle}deg)`,
              transition: spinning
                ? `transform ${WHEEL_SPIN_DURATION_MS / 1000}s cubic-bezier(.12,.9,.2,1)`
                : 'none',
            }}
            onTransitionEnd={() => {
              setSpinning(false);
              const idx = computeSelectedIndex(angle);
              setSelectedIndex(idx);
              setFinalPrize(
                plannedPrizeRef.current ?? sectors[idx]?.prize ?? null
              );
            }}
          >
            <svg
              viewBox="-52 -52 104 104"
              className="wheel-svg"
              role="img"
              aria-label={translate('jackpotWheel.wheelAria')}
            >
              <defs>
                {/* центральный «стеклянный» диск */}
                <radialGradient id="neonHub" cx="0" cy="0" r="1">
                  <stop offset="0%" stopColor="#f9eab0" />
                  <stop offset="55%" stopColor="#d7b765" />
                  <stop offset="100%" stopColor="#8b6424" />
                </radialGradient>

                {/* внутреннее свечение под секторами */}
                <radialGradient id="neonGlow" cx="0" cy="0" r="1">
                  <stop offset="0%" stopColor="rgba(56,189,248,.33)" />
                  <stop offset="60%" stopColor="rgba(15,23,42,.4)" />
                  <stop offset="100%" stopColor="rgba(15,23,42,1)" />
                </radialGradient>
              </defs>

              {/* фон колеса */}
              <circle cx="0" cy="0" r="50" fill="#020617" />
              <circle cx="0" cy="0" r="49" fill="url(#neonGlow)" />

              {/* сектора */}
              {sectors.map((s, i) => (
                <path
                  key={i}
                  d={donutPath(46.5, 20.5, s.start, s.end)}
                  fill={palette(i)}
                  stroke="rgba(15,23,42,.95)"
                  strokeWidth="0.7"
                />
              ))}

              {/* радиальные разделители */}
              {sectors.map((s, i) => {
                const [x1, y1] = polar(46.5, s.start);
                const [x2, y2] = polar(20.5, s.start);
                return (
                  <line
                    key={`sep-${i}`}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="rgba(148,163,184,.22)"
                    strokeWidth="0.7"
                  />
                );
              })}

              {/* подписи призов — вертикально вдоль радиуса */}
              {sectors.map((s, i) => {
                const centerAngle = normAngle(
                  s.start + cwDist(s.start, s.end) / 2
                );

                /** радиус, где лежит текст — можно крутить 26–30 */
                const labelRadius = 33;
                const [tx, ty] = polar(labelRadius, centerAngle);

                // поворот, чтобы текст смотрел внутрь
                const angleFromX = centerAngle - 90;

                const text = formatPrize(s.prize);
                const baseSize = 8.5;
                const len = String(text).length;
                let fontSize = baseSize;

                if (len >= 5) fontSize = baseSize - 2.0;
                else if (len === 4) fontSize = baseSize - 1.1;

                return (
                  <text
                    key={`label-${i}`}
                    x={tx}
                    y={ty}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(${angleFromX} ${tx} ${ty})`}
                    className="wheel-sector-label wheel-sector-label--neon"
                    fontSize={fontSize}
                  >
                    {text}
                  </text>
                );
              })}

              {/* внутреннее кольцо */}
              <circle
                cx="0"
                cy="0"
                r="20.5"
                fill="#020617"
                stroke="rgba(15,23,42,1)"
                strokeWidth="2"
              />
              {/* центральный диск */}
              <circle
                cx="0"
                cy="0"
                r="16.5"
                fill="url(#neonHub)"
                stroke="rgba(15,23,42,.85)"
                strokeWidth="1.2"
              />
              <circle cx="0" cy="0" r="6.5" fill="#020617" />
            </svg>

            {/* подсветка выбранного сектора */}
            {selectedIndex >= 0 && (() => {
              const s = sectors[selectedIndex];
              const d = donutPath(46.5, 20.5, s.start, s.end);
              return (
                <svg
                  viewBox="-52 -52 104 104"
                  className="wheel-svg selected-overlay selected-overlay--neon"
                  aria-hidden
                >
                  <path
                    d={d}
                    fill="none"
                    stroke="rgba(56,189,248,.9)"
                    strokeWidth="1.1"
                  />
                </svg>
              );
            })()}
          </div>
        </div>

        {/* нижняя плашка с призом */}
        {!spinning && finalPrize != null && (
          <div className="wheel-footer">
            <div className="wheel-prize-pill">
              <span className="wheel-prize-label">
                {translate('jackpotWheel.prizeLabel')}
              </span>
              <span className="wheel-prize-value">
                {formatPrize(finalPrize)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// import tournamentSocket from './tournamentSocket';
// import longTournamentSocket from './longTournamentSocket';
// import './SpinWheelOverlay.css';

// /* 0° — вверх. Стрелка фиксирована на 270° (9 часов). */
// const POINTER_DEG = 270;
// const CLIENT_ANGLE_CORRECTION = 5;





// /* Единая палитра */
// const elegantPalette = [
//   '#1b2330', '#141b26', '#1f2a3a', '#151c27',
//   '#1c2533', '#141b26', '#202b3b', '#141a24',
//   '#1b2330', '#141b26', '#1f2a3a', '#151c27',
// ];
// const paletteByBuyIn = new Proxy({}, { get: () => elegantPalette });

// /* Карты секторов (центры, в градусах) */
// const sectorSpecsByBuyIn = {
//   1: [
//     { prize: 2,   mid: 225 }, { prize: 3,   mid: 105 }, { prize: 4,   mid: 345 },
//     { prize: 5,   mid: 45  }, { prize: 8,   mid: 285 }, { prize: 10,  mid: 195 },
//     { prize: 12,  mid: 165 }, { prize: 15,  mid: 360 }, { prize: 20,  mid: 315 },
//     { prize: 30,  mid: 135 }, { prize: 50,  mid: 75  }, { prize: 100, mid: 255 },
//   ],
//   2: [
//     { prize: 4,   mid: 15  }, { prize: 6,   mid: 45  }, { prize: 8,   mid: 75  },
//     { prize: 10,  mid: 105 }, { prize: 16,  mid: 135 }, { prize: 20,  mid: 165 },
//     { prize: 24,  mid: 195 }, { prize: 30,  mid: 225 }, { prize: 40,  mid: 255 },
//     { prize: 60,  mid: 285 }, { prize: 100, mid: 315 }, { prize: 200, mid: 345 },
//   ],
//   5: [
//     { prize: 10,  mid: 15  }, { prize: 15,  mid: 45  }, { prize: 20,  mid: 75  },
//     { prize: 25,  mid: 105 }, { prize: 30,  mid: 135 }, { prize: 50,  mid: 165 },
//     { prize: 80,  mid: 195 }, { prize: 100, mid: 225 }, { prize: 150, mid: 255 },
//     { prize: 200, mid: 285 }, { prize: 300, mid: 315 }, { prize: 500, mid: 345 },
//   ],

//   10: [
//     { prize: 20,  mid: 15  }, { prize: 25,  mid: 45  }, { prize: 30,  mid: 75  },
//     { prize: 50,  mid: 105 }, { prize: 80,  mid: 135 }, { prize: 150,  mid: 165 },
//     { prize: 300,  mid: 195 }, { prize: 500, mid: 225 }, { prize: 600, mid: 255 },
//     { prize: 700, mid: 285 }, { prize: 800, mid: 315 }, { prize: 1000, mid: 345 },
//   ],
  
//   20: [
//     { prize: 40,  mid: 15  }, { prize: 50,  mid: 45  }, { prize: 60,  mid: 75  },
//     { prize: 80,  mid: 105 }, { prize: 100, mid: 135 }, { prize: 140, mid: 165 },
//     { prize: 200, mid: 195 }, { prize: 300, mid: 225 }, { prize: 500, mid: 255 },
//     { prize: 1000, mid: 285 }, { prize: 1500, mid: 315 }, { prize: 2000, mid: 345 },
//   ],
//   30: [
//     { prize: 60,  mid: 15  }, { prize: 70,  mid: 45  }, { prize: 80,  mid: 75  },
//     { prize: 90,  mid: 105 }, { prize: 150, mid: 135 }, { prize: 220, mid: 165 },
//     { prize: 250, mid: 195 }, { prize: 500, mid: 225 }, { prize: 800, mid: 255 },
//     { prize: 1000, mid: 285 }, { prize: 1500, mid: 315 }, { prize: 3000, mid: 345 },
//   ],
//   50: [
//     { prize: 100, mid: 105 }, { prize: 150, mid: 305 }, { prize: 200, mid: 225 },
//     { prize: 250, mid: 75  }, { prize: 350, mid: 255 }, { prize: 600, mid: 165 },
//     { prize: 1000, mid: 15  }, { prize: 1500, mid: 285 }, { prize: 2000, mid: 45  },
//     { prize: 3000, mid: 335 }, { prize: 3500, mid: 195 }, { prize: 5000, mid: 135 },
//   ],
// };

// /* ─ helpers геометрии ─ */
// const normAngle = (a) => { let x = a % 360; if (x < 0) x += 360; return x; };
// const degToRadForSvg = (deg0Up) => ((deg0Up - 90) * Math.PI) / 180;
// const polar = (r, aDeg) => { const a = degToRadForSvg(aDeg); return [r * Math.cos(a), r * Math.sin(a)]; };
// const cwDist = (a, b) => { const d = normAngle(b) - normAngle(a); return d < 0 ? d + 360 : d; };

// /* Формат: $1,000 */
// const formatPrize = (v) => `$${new Intl.NumberFormat('en-US').format(Number(v) || 0)}`;

// /* Построение клиньев по центрам */
// function buildSectors(midSpecs) {
//   const s = [...midSpecs].sort((x, y) => x.mid - y.mid);
//   const n = s.length, out = [];
//   for (let i = 0; i < n; i++) {
//     const prev = s[(i - 1 + n) % n].mid;
//     const cur  = s[i].mid;
//     const next = s[(i + 1) % n].mid;
//     const start = normAngle(cur - cwDist(prev, cur) / 2);
//     const end   = normAngle(cur + cwDist(cur, next) / 2);
//     out.push({ prize: s[i].prize, mid: normAngle(cur), start, end, sweep: cwDist(start, end) || 360 });
//   }
//   return out.sort((a, b) => a.start - b.start);
// }

// function donutPath(R, r, a1, a2) {
//   const largeArc = cwDist(a1, a2) > 180 ? 1 : 0;
//   const [x1, y1] = polar(R, a1); const [x2, y2] = polar(R, a2);
//   const [x3, y3] = polar(r, a2); const [x4, y4] = polar(r, a1);
//   return [
//     `M ${x1.toFixed(3)} ${y1.toFixed(3)}`,
//     `A ${R} ${R} 0 ${largeArc} 1 ${x2.toFixed(3)} ${y2.toFixed(3)}`,
//     `L ${x3.toFixed(3)} ${y3.toFixed(3)}`,
//     `A ${r} ${r} 0 ${largeArc} 0 ${x4.toFixed(3)} ${y4.toFixed(3)}`,
//     'Z',
//   ].join(' ');
// }

// /* angle в секторе? */
// const inSector = (angle, start, end, eps = 0.5) => cwDist(start, angle) <= cwDist(start, end) + eps;

// /* Нормализуем buy-in из числа/строки */
// const parseBuyIn = (v) => {
//   const m = String(v ?? '').match(/\d+(\.\d+)?/);
//   return m ? Number(m[0]) : 1;
// };

// export default function SpinWheelOverlay() {
//   const [show, setShow] = useState(false);
//   const [angle, setAngle] = useState(0);
//   const [spinning, setSpinning] = useState(false);
//   const [finalPrize, setFinalPrize] = useState(null);
//   const [currentBuyIn, setCurrentBuyIn] = useState(1);
//   const [selectedIndex, setSelectedIndex] = useState(-1);
//   const hideTimerRef = useRef(null);
//   const plannedPrizeRef = useRef(null);
//   /* Клинья по buy-in */
//   const sectors = useMemo(() => {
//     const bi = parseBuyIn(currentBuyIn);
//     const mids = sectorSpecsByBuyIn[bi] || sectorSpecsByBuyIn[1];
//     if (!sectorSpecsByBuyIn[bi]) {
//       console.warn('[SpinWheel] нет набора секторов для buyIn=', currentBuyIn, '→ используем $1');
//     }
//     return buildSectors(mids);
//   }, [currentBuyIn]);

//   const palette = useMemo(() => {
//     const p = paletteByBuyIn[parseBuyIn(currentBuyIn)] || elegantPalette;
//     return (idx) => p[idx % p.length];
//   }, [currentBuyIn]);

//   const computeSelectedIndex = useCallback((rot) => {
//     const pointerInWheel = normAngle(POINTER_DEG - normAngle(rot));
//     return sectors.findIndex(s => inSector(pointerInWheel, s.start, s.end));
//   }, [sectors]);

//   useEffect(() => {
//     function handleSpinWheel({ angle, prize, buyIn }) {
//       setShow(true);
//       setAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);
//       setSelectedIndex(-1);
//       setCurrentBuyIn(parseBuyIn(buyIn));

//       setTimeout(() => {
//         setAngle((Number(angle) || 0) + CLIENT_ANGLE_CORRECTION);
//         setSpinning(true);
//       }, 100);

//       if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
//       hideTimerRef.current = setTimeout(() => setShow(false), 12000);

//       // setFinalPrize(prize);
//       plannedPrizeRef.current = prize;
//     }
//     tournamentSocket.on('spinWheel', handleSpinWheel);
//     longTournamentSocket?.on?.('spinWheel', handleSpinWheel);
//       return () => {
//     tournamentSocket.off('spinWheel', handleSpinWheel);
//   longTournamentSocket?.off?.('spinWheel', handleSpinWheel);
//   };
// }, []);

//   useEffect(() => {
//     if (!show) {
//       setAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);
//       setSelectedIndex(-1);
//       plannedPrizeRef.current = null;
//       if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
//     }
//   }, [show]);

//   const uidRef = useRef(`wheel-${Math.random().toString(36).slice(2)}`);
//   const UID = uidRef.current;

//   if (!show) return null;

//   const buyInPretty = `$${parseBuyIn(currentBuyIn)}`;

//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-card">
//         <div className="wheel-title">TOURNAMENT • BUY-IN {buyInPretty}</div>

//         <div className="wheel-container">
//           <div className="outer-ring" aria-hidden />
//           <div className="wheel-pointer" aria-label="pointer" />

//           <div
//             className="wheel"
//             style={{
//               transform: `rotate(${angle}deg)`,
//               transition: spinning ? 'transform 5.4s cubic-bezier(.12,.9,.2,1)' : 'none',
//             }}
//           onTransitionEnd={() => {
//   setSpinning(false);
//   const idx = computeSelectedIndex(angle);
//   setSelectedIndex(idx);
//   setFinalPrize(plannedPrizeRef.current ?? sectors[idx]?.prize ?? null);
// }}

//           >
//             <svg viewBox="-52 -52 104 104" className="wheel-svg" role="img" aria-label="Spin wheel">
//               <defs>
//                 <radialGradient id="hubGrad" cx="0" cy="0" r="1">
//                   <stop offset="0%"  stopColor="#f5dba5"/>
//                   <stop offset="60%" stopColor="#d0a957"/>
//                   <stop offset="100%" stopColor="#8a6a2a"/>
//                 </radialGradient>
//                 <radialGradient id="glow" cx="0" cy="0" r="1">
//                   <stop offset="0%"  stopColor="rgba(255,255,255,.18)"/>
//                   <stop offset="70%" stopColor="rgba(255,255,255,.06)"/>
//                   <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
//                 </radialGradient>
//               </defs>

//               {/* подложка */}
//               <circle cx="0" cy="0" r="50" fill="#0c111b" />
//               <circle cx="0" cy="0" r="50" fill="url(#glow)" />

//               {/* клинья */}
//               {sectors.map((s, i) => (
//                 <path
//                   key={i}
//                   d={donutPath(48, 21, s.start, s.end)}
//                   fill={palette(i)}
//                   stroke="rgba(0,0,0,.35)"
//                   strokeWidth="0.5"
//                 />
//               ))}

//               {/* разделители */}
//               {sectors.map((s, i) => {
//                 const [x1,y1] = polar(48, s.start);
//                 const [x2,y2] = polar(21, s.start);
//                 return (
//                   <line
//                     key={`sep-${i}`}
//                     x1={x1} y1={y1} x2={x2} y2={y2}
//                     stroke="rgba(255,255,255,.06)" strokeWidth="0.6"
//                   />
//                 );
//               })}

//               {/* дуги для текста */}
//               <defs>
//                 {sectors.map((s, i) => {
//                   const PAD = 3;                 // чуть отступаем от границ клина
//                   const a1 = normAngle(s.start + PAD);
//                   const a2 = normAngle(s.end   - PAD);
//                   const R  = 33;                 // радиус подписи
//                   const largeArc = cwDist(a1, a2) > 180 ? 1 : 0;
//                   const [x1, y1] = polar(R, a1);
//                   const [x2, y2] = polar(R, a2);
//                   const d = [
//                     `M ${x1.toFixed(3)} ${y1.toFixed(3)}`,
//                     `A ${R} ${R} 0 ${largeArc} 1 ${x2.toFixed(3)} ${y2.toFixed(3)}`
//                   ].join(' ');
//                   return <path key={`arc-${i}`} id={`${UID}-arc-${i}`} d={d} fill="none"/>;
//                 })}
//               </defs>

//               {/* подписи призов — теперь всегда помещаются */}
//               {sectors.map((s, i) => {
//                 const PAD = 3;
//                 const R = 33;
//                 const a1 = normAngle(s.start + PAD);
//                 const a2 = normAngle(s.end   - PAD);
//                 const theta = cwDist(a1, a2) * Math.PI / 180;  // ширина дуги, рад
//                 const arcLen = Math.max(R * theta, 8);         // «длина строки»
//                 const text = formatPrize(s.prize);

//                 // размер шрифта по длине дуги (чуть варьируем, но без крайностей)
//                 const fs = Math.max(6, Math.min(9.5, arcLen / 6.5));

//                 return (
//                   <text
//                     key={`label-${i}`}
//                     fontSize={fs}
//                     fontWeight="800"
//                     fill="#fff"
//                     stroke="rgba(0,0,0,.55)"
//                     strokeWidth="0.8"
//                     textAnchor="middle"
//                     style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: 0.25 }}
//                   >
//                     <textPath
//                       href={`#${UID}-arc-${i}`}
//                       xlinkHref={`#${UID}-arc-${i}`}
//                       startOffset="50%"
//                       dominantBaseline="middle"
//                       lengthAdjust="spacingAndGlyphs"
//                       textLength={arcLen.toFixed(1)}   // ← гарантируем, что строка влезет в дугу
//                     >
//                       {text}
//                     </textPath>
//                   </text>
//                 );
//               })}

//               {/* центр */}
//               <circle cx="0" cy="0" r="17.5" fill="url(#hubGrad)" stroke="rgba(0,0,0,.55)" strokeWidth="1.2"/>
//               <circle cx="0" cy="0" r="7" fill="#0c111b" />
//             </svg>

//             {/* подсветка выбранного сектора */}
//             {selectedIndex >= 0 && (() => {
//               const s = sectors[selectedIndex];
//               const d = donutPath(48, 21, s.start, s.end);
//               return (
//                 <svg viewBox="-52 -52 104 104" className="wheel-svg selected-overlay" aria-hidden>
//                   <path d={d} fill="rgba(212,175,55,.18)" stroke="rgba(212,175,55,.85)" strokeWidth="1.2"/>
//                 </svg>
//               );
//             })()}
//           </div>

//           {/* бейдж у стрелки */}
//           {!spinning && selectedIndex >= 0 && (
//             <div className="pointer-badge">
//               {formatPrize(finalPrize ?? sectors[selectedIndex]?.prize ?? 0)}
//             </div>
//           )}
//         </div>

//         {!spinning && finalPrize != null && (
//           <div className="prize-chip">Выпал приз: <b>{formatPrize(finalPrize)}</b></div>
//         )}
//       </div>
//     </div>
//   );
// }





// import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// import tournamentSocket from './tournamentSocket';
// import './SpinWheelOverlay.css';

// /* 0° — вверх, по часовой. Стрелка фиксирована на 270° (9 часов). */
// const POINTER_DEG = 270;
// const CLIENT_ANGLE_CORRECTION = 5;

// /* Палитра — строгие тёмные клинья. Тот же набор для всех buy-in, чтобы стиль был единый. */
// const elegantPalette = [
//   '#1b2330', '#141b26', '#1f2a3a', '#151c27',
//   '#1c2533', '#141b26', '#202b3b', '#141a24',
//   '#1b2330', '#141b26', '#1f2a3a', '#151c27',
// ];
// const paletteByBuyIn = new Proxy({}, {
//   get: () => elegantPalette
// });

// /* Сектора (центры) — как у тебя. Границы считаем от середины к соседям. */
// const sectorSpecsByBuyIn = {
//   1: [
//     { prize: 2,   mid: 225 }, { prize: 3,   mid: 105 }, { prize: 4,   mid: 345 },
//     { prize: 5,   mid: 45  }, { prize: 8,   mid: 285 }, { prize: 10,  mid: 195 },
//     { prize: 12,  mid: 165 }, { prize: 15,  mid: 360 }, { prize: 20,  mid: 315 },
//     { prize: 30,  mid: 135 }, { prize: 50,  mid: 75  }, { prize: 100, mid: 255 },
//   ],
//   2: [
//     { prize: 4,   mid: 15  }, { prize: 6,   mid: 45  }, { prize: 8,   mid: 75  },
//     { prize: 10,  mid: 105 }, { prize: 16,  mid: 135 }, { prize: 20,  mid: 165 },
//     { prize: 24,  mid: 195 }, { prize: 30,  mid: 225 }, { prize: 40,  mid: 255 },
//     { prize: 60,  mid: 285 }, { prize: 100, mid: 315 }, { prize: 200, mid: 345 },
//   ],
//   5: [
//     { prize: 10,  mid: 15  }, { prize: 15,  mid: 45  }, { prize: 20,  mid: 75  },
//     { prize: 25,  mid: 105 }, { prize: 30,  mid: 135 }, { prize: 50,  mid: 165 },
//     { prize: 80,  mid: 195 }, { prize: 100, mid: 225 }, { prize: 200, mid: 255 },
//     { prize: 300, mid: 285 }, { prize: 500, mid: 315 }, { prize: 1000, mid: 345 },
//   ],
//   10: [
//     { prize: 10,  mid: 15  }, { prize: 15,  mid: 45  }, { prize: 20,  mid: 75  },
//     { prize: 25,  mid: 105 }, { prize: 30,  mid: 135 }, { prize: 50,  mid: 165 },
//     { prize: 80,  mid: 195 }, { prize: 100, mid: 225 }, { prize: 200, mid: 255 },
//     { prize: 300, mid: 285 }, { prize: 500, mid: 315 }, { prize: 1000, mid: 345 },
//   ],
//   20: [
//     { prize: 40,  mid: 15  }, { prize: 50,  mid: 45  }, { prize: 60,  mid: 75  },
//     { prize: 80,  mid: 105 }, { prize: 100, mid: 135 }, { prize: 140, mid: 165 },
//     { prize: 200, mid: 195 }, { prize: 300, mid: 225 }, { prize: 500, mid: 255 },
//     { prize: 1000,mid: 285 }, { prize: 1500,mid: 315 }, { prize: 2000,mid: 345 },
//   ],
//   30: [
//     { prize: 60,  mid: 15  }, { prize: 70,  mid: 45  }, { prize: 80,  mid: 75  },
//     { prize: 90,  mid: 105 }, { prize: 150, mid: 135 }, { prize: 220, mid: 165 },
//     { prize: 250, mid: 195 }, { prize: 500, mid: 225 }, { prize: 800, mid: 255 },
//     { prize: 1000,mid: 285 }, { prize: 1500,mid: 315 }, { prize: 3000,mid: 345 },
//   ],
//   50: [
//     { prize: 100, mid: 105 }, { prize: 150, mid: 305 }, { prize: 200, mid: 225 },
//     { prize: 250, mid: 75  }, { prize: 350, mid: 255 }, { prize: 600, mid: 165 },
//     { prize: 1000,mid: 15  }, { prize: 1500,mid: 285 }, { prize: 2000,mid: 45  },
//     { prize: 3000,mid: 335 }, { prize: 3500,mid: 195 }, { prize: 5000,mid: 135 },
//   ],
// };

// /* ───────── helpers для углов/геометрии ───────── */
// const normAngle = (a) => { let x = a % 360; if (x < 0) x += 360; return x; };
// const degToRadForSvg = (deg0Up) => ((deg0Up - 90) * Math.PI) / 180;
// const polar = (r, aDeg) => { const a = degToRadForSvg(aDeg); return [r * Math.cos(a), r * Math.sin(a)]; };
// const cwDist = (a, b) => { const d = normAngle(b) - normAngle(a); return d < 0 ? d + 360 : d; };


// // формат подписи приза: $1,000
// const formatPrize = (v) => `$${new Intl.NumberFormat('en-US').format(v)}`;
// // единый размер шрифта для всех секторов
// const LABEL_FS = 10;






// function buildSectors(midSpecs) {
//   const s = [...midSpecs].sort((x, y) => x.mid - y.mid);
//   const n = s.length, out = [];
//   for (let i = 0; i < n; i++) {
//     const prev = s[(i - 1 + n) % n].mid;
//     const cur  = s[i].mid;
//     const next = s[(i + 1) % n].mid;
//     const start = normAngle(cur - cwDist(prev, cur)/2);
//     const end   = normAngle(cur + cwDist(cur, next)/2);
//     out.push({ prize: s[i].prize, mid: normAngle(cur), start, end, sweep: cwDist(start, end) || 360 });
//   }
//   return out.sort((a, b) => a.start - b.start);
// }

// function donutPath(R, r, a1, a2) {
//   const largeArc = cwDist(a1, a2) > 180 ? 1 : 0;
//   const [x1, y1] = polar(R, a1); const [x2, y2] = polar(R, a2);
//   const [x3, y3] = polar(r, a2); const [x4, y4] = polar(r, a1);
//   return [
//     `M ${x1.toFixed(3)} ${y1.toFixed(3)}`,
//     `A ${R} ${R} 0 ${largeArc} 1 ${x2.toFixed(3)} ${y2.toFixed(3)}`,
//     `L ${x3.toFixed(3)} ${y3.toFixed(3)}`,
//     `A ${r} ${r} 0 ${largeArc} 0 ${x4.toFixed(3)} ${y4.toFixed(3)}`,
//     'Z',
//   ].join(' ');
// }

// /* проверка: попадает ли angle внутрь дуги [start..end] по часовой */
// const inSector = (angle, start, end, eps = 0.5) => cwDist(start, angle) <= cwDist(start, end) + eps;

// export default function SpinWheelOverlay() {
//   const [show, setShow] = useState(false);
//   const [angle, setAngle] = useState(0);
//   const [spinning, setSpinning] = useState(false);
//   const [finalPrize, setFinalPrize] = useState(null);
//   const [currentBuyIn, setCurrentBuyIn] = useState(1);
//   const [selectedIndex, setSelectedIndex] = useState(-1); // сектор под стрелкой после остановки
//   const hideTimerRef = useRef(null);

//   // const sectors = useMemo(() => {
//   //   const mids = sectorSpecsByBuyIn[currentBuyIn] || sectorSpecsByBuyIn[1];
//   //   return buildSectors(mids);
//   // }, [currentBuyIn]);

//   const normBuyIn = (v) => Number(v) || 1;
//   const sectors = useMemo(() => {
//     const bi = normBuyIn(currentBuyIn);
//     const mids = sectorSpecsByBuyIn[bi] || sectorSpecsByBuyIn[1];
//     if (!sectorSpecsByBuyIn[bi]) {
//       console.warn('[SpinWheel] нет набора секторов для buyIn=', currentBuyIn, '→ используем $1');
//     }
//     return buildSectors(mids);
//   }, [currentBuyIn]);


//   const palette = useMemo(() => {
//     const p = paletteByBuyIn[currentBuyIn] || elegantPalette;
//     return (idx) => p[idx % p.length];
//   }, [currentBuyIn]);

//   const computeSelectedIndex = useCallback((rot) => {
//     const pointerInWheel = normAngle(POINTER_DEG - normAngle(rot));
//     return sectors.findIndex(s => inSector(pointerInWheel, s.start, s.end));
//   }, [sectors]);

//   useEffect(() => {
//     function handleSpinWheel({ angle, prize, buyIn }) {
//       setShow(true);
//       setAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);
//       setSelectedIndex(-1);
//       setCurrentBuyIn(Number(buyIn) || 1);

//       setTimeout(() => {
//         setAngle(angle + CLIENT_ANGLE_CORRECTION);
//         setSpinning(true);
//       }, 100);

//       if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
//       hideTimerRef.current = setTimeout(() => setShow(false), 12000);

//       setFinalPrize(prize);
//     }
//     tournamentSocket.on('spinWheel', handleSpinWheel);
//     return () => tournamentSocket.off('spinWheel', handleSpinWheel);
//   }, []);

//   useEffect(() => {
//     if (!show) {
//       setAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);
//       setSelectedIndex(-1);
//       if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
//     }
//   }, [show]);




// // уникальный префикс для id в <textPath>, чтобы не конфликтовать с другими колесами
// const uidRef = useRef(`wheel-${Math.random().toString(36).slice(2)}`);
// const UID = uidRef.current;




//   if (!show) return null;

//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-card">
//         <div className="wheel-title">Tournament • Buy-in ${currentBuyIn}</div>

//         <div className="wheel-container">
//           {/* Внешнее золотое кольцо-обод */}
//           <div className="outer-ring" aria-hidden />

//           {/* Указатель (стрелка) на 9 часах */}
//           <div className="wheel-pointer" aria-label="pointer" />

//           {/* Колесо (вращаем целиком) */}
//           <div
//             className="wheel"
//             style={{
//               transform: `rotate(${angle}deg)`,
//               transition: spinning ? 'transform 5.4s cubic-bezier(.12,.9,.2,1)' : 'none',
//             }}
//             onTransitionEnd={() => {
//               setSpinning(false);
//               setSelectedIndex(computeSelectedIndex(angle));
//             }}
//           >
//             <svg viewBox="-52 -52 104 104" className="wheel-svg" role="img" aria-label="Spin wheel">
//               <defs>
//                 <radialGradient id="hubGrad" cx="0" cy="0" r="1">
//                   <stop offset="0%"  stopColor="#f5dba5"/>
//                   <stop offset="60%" stopColor="#d0a957"/>
//                   <stop offset="100%" stopColor="#8a6a2a"/>
//                 </radialGradient>
//                 <radialGradient id="glow" cx="0" cy="0" r="1">
//                   <stop offset="0%"  stopColor="rgba(255,255,255,.18)"/>
//                   <stop offset="70%" stopColor="rgba(255,255,255,.06)"/>
//                   <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
//                 </radialGradient>
//               </defs>

//               {/* подложка */}
//               <circle cx="0" cy="0" r="50" fill="#0c111b" />
//               <circle cx="0" cy="0" r="50" fill="url(#glow)" />

//               {/* клинья */}
//               {sectors.map((s, i) => (
//                 <path
//                   key={i}
//                   d={donutPath(48, 21, s.start, s.end)}
//                   fill={palette(i)}
//                   stroke="rgba(0,0,0,.35)"
//                   strokeWidth="0.5"
//                 />
//               ))}

//               {/* тонкий разделительный штрих у границ (делает чище) */}
//               {sectors.map((s, i) => {
//                 const [x1,y1] = polar(48, s.start);
//                 const [x2,y2] = polar(21, s.start);
//                 return (
//                   <line
//                     key={`sep-${i}`}
//                     x1={x1} y1={y1} x2={x2} y2={y2}
//                     stroke="rgba(255,255,255,.06)" strokeWidth="0.6"
//                   />
//                 );
//               })}

//               {/* подписи призов (чёткие, без цветных плашек) */}
//              {/* Дуги для надписей (в defs) + сами надписи по дуге */}
// <defs>
//   {sectors.map((s, i) => {
//     // чуть отступаем от границ сектора, чтобы точно не задевать разделители
//     const pad = 4;                    // градусов «внутренней» рамки текста
//     const a1 = normAngle(s.start + pad);
//     const a2 = normAngle(s.end   - pad);
//     const Rlabel = 33;                // радиус, где лежит надпись (между внешним краем и центром)
//     const largeArc = cwDist(a1, a2) > 180 ? 1 : 0;
//     const [x1, y1] = polar(Rlabel, a1);
//     const [x2, y2] = polar(Rlabel, a2);
//     const d = [
//       `M ${x1.toFixed(3)} ${y1.toFixed(3)}`,
//       `A ${Rlabel} ${Rlabel} 0 ${largeArc} 1 ${x2.toFixed(3)} ${y2.toFixed(3)}`
//     ].join(' ');
//     return <path key={`arc-${i}`} id={`${UID}-arc-${i}`} d={d} fill="none"/>;
//   })}
// </defs>

// {/* Текст по дуге — всегда внутри клина, по центру */}
// {sectors.map((s, i) => {
//   // прикинем длину дуги, чтобы подобрать размер шрифта
//   const pad = 4;
//   const Rlabel = 33;
//   const theta = cwDist(s.start + pad, s.end - pad) * Math.PI / 180; // радианы
//   const arcLen = Rlabel * theta;               // приблизительная длина дуги
//   // базовый размер и лёгкая авто-адаптация: длинные слова — немного меньше шрифт
//   const base = 8;                              // px
//   const fs = Math.max(6, Math.min(9, (arcLen / (String(s.prize).length * 6)) * base));

//   return (
//  <text
//   key={`label-${i}`}
//   fontSize={LABEL_FS}
//   fontWeight="800"
//   fill="#fff"
//   stroke="rgba(0,0,0,.55)"
//   strokeWidth="0.8"
//   textAnchor="middle"
//   style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: 0.3 }}
// >
//   <textPath
//     href={`#${UID}-arc-${i}`}
//     xlinkHref={`#${UID}-arc-${i}`}        /* совместимость */
//     startOffset="50%"
//     dominantBaseline="middle"
//     lengthAdjust="spacing"                /* только интервал, без деформации глифов */
//   >
//     {formatPrize(s.prize)}                {/* <-- теперь $ перед числом */}
//   </textPath>
// </text>

//   );
// })}


//               {/* центральная «шайба» под золото */}
//               <circle cx="0" cy="0" r="17.5" fill="url(#hubGrad)" stroke="rgba(0,0,0,.55)" strokeWidth="1.2"/>
//               <circle cx="0" cy="0" r="7" fill="#0c111b" />
//             </svg>

//             {/* подсветка выбранного сектора — поверх SVG (аккуратное золото) */}
//             {selectedIndex >= 0 && (() => {
//               const s = sectors[selectedIndex];
//               const d = donutPath(48, 21, s.start, s.end);
//               return (
//                 <svg viewBox="-52 -52 104 104" className="wheel-svg selected-overlay" aria-hidden>
//                   <path d={d} fill="rgba(212,175,55,.18)" stroke="rgba(212,175,55,.85)" strokeWidth="1.2"/>
//                 </svg>
//               );
//             })()}
//           </div>

//           {/* подпись у стрелки после остановки */}
//           {(selectedIndex >= 0 || finalPrize) && (
//            <div className="pointer-badge">
//             {formatPrize(finalPrize ?? sectors[selectedIndex]?.prize ?? 0)}
//           </div>
//           )}
//         </div>

//          {finalPrize && (
//             <div className="prize-chip">Выпал приз: <b>{formatPrize(finalPrize)}</b></div>
//           )}
//       </div>
//     </div>
//   );
// }






// import React, { useState, useEffect, useRef } from 'react';
// import tournamentSocket from './tournamentSocket';
// import './SpinWheelOverlay.css';

// // Мапа для внутреннего колеса (по байину)
// const wheelImagesMap = {
//   1:  '/img/CentrKrug.png',
//   2:  '/img/5556.jpg',
//   5:  '/img/wheelForBuyIn5.png',
//   10: '/img/wheelForBuyIn10.png',
//   20: '/img/wheelForBuyIn20.png',
//   30: '/img/wheelForBuyIn30.png',
//   50: '/img/Vnutri50.png',
// };
// const defaultWheelImage = '/img/wheelDefault.png';

// // Мапа для наружного кольца (по байину)
// const outerRingImagesMap = {
//   1:  '/img/NarujKrug.png',
//   2:  '/img/outerRing2.png',
//   5:  '/img/outerRing5.png',
//   10: '/img/outerRing10.png',
//   20: '/img/outerRing20.png',
//   30: '/img/outerRing30.png',
//   50: '/img/ВН50.png',
// };
// const defaultOuterRingImage = '/img/NarujKrug.png';

// // Если «стрелка» реально расположена на 9 часах, обычно offset=180
// // Но здесь мы уже не используем offset внутри компоненты,
// // потому что угол приходит прямо от сервера.
// const OFFSET = 180;

// // Дополнительное «микросмещение» (чтобы не попадать ровно между секторами).
// // Можно ставить +5 или -5, в зависимости от того, куда надо «сдвинуть» остановку.
// const CLIENT_ANGLE_CORRECTION = 5;

// export default function SpinWheelOverlay() {
// // export default function SpinWheelOverlay({ visible, angle, prize, buyIn, onClose }) {

//   const [show, setShow] = useState(false);
//   const [angle, setAngle] = useState(0);
//   const [spinning, setSpinning] = useState(false);
//   const [finalPrize, setFinalPrize] = useState(null);

//   // Запоминаем текущий байин (1,2,5,10,20,30,50)
//   const [currentBuyIn, setCurrentBuyIn] = useState(1);

//   // Таймер для скрытия
//   const hideTimerRef = useRef(null);

//   useEffect(() => {
//     function handleSpinWheel({ angle, prize, buyIn, message }) {
//       console.log('[SpinWheelOverlay] => spinWheel:', { angle, prize, buyIn, message });

//       // Показываем оверлей
//       setShow(true);
//       // Сбрасываем предыдущие
//       setAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);

//       // Устанавливаем buyIn, чтобы отобразить нужное колесо
//       setCurrentBuyIn(buyIn || 1);

//       // Запускаем вращение чуть позже (100 мс)
//       setTimeout(() => {
//         // Прибавляем 5 градусов к углу, который пришёл с сервера
//         const correctedAngle = angle + CLIENT_ANGLE_CORRECTION;
//         setAngle(correctedAngle);
//         setSpinning(true);
//       }, 100);

//       // Запускаем таймер скрытия через 12 секунд
//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//       }
//       hideTimerRef.current = setTimeout(() => {
//         setShow(false);
//       }, 12000);

//       // Запоминаем финальный приз
//       setFinalPrize(prize);
//     }

//     // Подписка
//     tournamentSocket.on('spinWheel', handleSpinWheel);

//     return () => {
//       // Отписка при размонтировании
//       tournamentSocket.off('spinWheel', handleSpinWheel);
//     };
//   }, []);

//   // Когда оверлей скрывается — сбрасываем значения
//   useEffect(() => {
//     if (!show) {
//       setAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);

//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//     }
//   }, [show]);

//   // Если скрыто, вообще не рендерим ничего
//   if (!show) return null;

//   // Определяем картинки для внутреннего и внешнего колеса
//   const wheelImage = wheelImagesMap[currentBuyIn] || defaultWheelImage;
//   const outerRingImage = outerRingImagesMap[currentBuyIn] || defaultOuterRingImage;

//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-container">
//         {/* Наружное кольцо */}
//         <img
//           className="outer-ring"
//           src={outerRingImage}
//           alt="Outer ring"
//         />

//         {/* Внутреннее колесо */}
//         <img
//           className="inner-circle"
//           src={wheelImage}
//           alt="Inner circle"
//           style={{
//             transform: `rotate(${angle}deg)`,
//             transition: spinning ? 'transform 5s ease-out' : 'none',
//           }}
//         />
//       </div>

//       {/* Текст о призе */}
//       {finalPrize && (
//         <div className="prize-message">
//           Вам выпал приз: <strong>{finalPrize}</strong>
//         </div>
//       )}
//     </div>
//   );
// }













// import React, { useState, useEffect, useRef } from 'react';
// import tournamentSocket from './tournamentSocket';
// import './SpinWheelOverlay.css';

// // Мапа для внутреннего колеса (по байину)
// const wheelImagesMap = {
//   1:  '/img/CentrKrug.png',
//   2:  '/img/5556.jpg',
//   5:  '/img/wheelForBuyIn5.png',
//   10: '/img/wheelForBuyIn10.png',
//   20: '/img/wheelForBuyIn20.png',
//   30: '/img/wheelForBuyIn30.png',
//   50: '/img/Vnutr50.png',
// };
// const defaultWheelImage = '/img/wheelDefault.png';

// // Мапа для наружного кольца (по байину)
// const outerRingImagesMap = {
//   1:  '/img/NarujKrug.png',
//   2:  '/img/outerRing2.png',
//   5:  '/img/outerRing5.png',
//   10: '/img/outerRing10.png',
//   20: '/img/outerRing20.png',
//   30: '/img/outerRing30.png',
//   50: '/img/Vnesh50.png',
// };
// const defaultOuterRingImage = '/img/NarujKrug.png';

// // Если указатель «на 9 часов» — используем offset=180 (если нужно)
// const OFFSET = 180;

// export default function SpinWheelOverlay() {
//   const [show, setShow] = useState(false);
//   const [angle, setAngle] = useState(0);
//   const [spinning, setSpinning] = useState(false);
//   const [finalPrize, setFinalPrize] = useState(null);
//   const [currentBuyIn, setCurrentBuyIn] = useState(1);

//   const hideTimerRef = useRef(null);

//   useEffect(() => {
//     function handleSpinWheel({ angle, prize, buyIn, message }) {
//       console.log('[SpinWheelOverlay] => spinWheel:', { angle, prize, buyIn, message });

//       setShow(true);
//       setAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);

//       // Запоминаем байин
//       setCurrentBuyIn(buyIn || 1);

//       // Задержка для старта анимации
//       setTimeout(() => {
//         setAngle(angle);
//         setSpinning(true);
//       }, 100);

//       // Таймер скрытия через 12с
//       if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
//       hideTimerRef.current = setTimeout(() => {
//         setShow(false);
//       }, 12000);

//       setFinalPrize(prize);
//     }

//     // Подписываемся
//     tournamentSocket.on('spinWheel', handleSpinWheel);

//     return () => {
//       // Отписываемся
//       tournamentSocket.off('spinWheel', handleSpinWheel);
//     };
//   }, []);

//   // Скрываем и сбрасываем
//   useEffect(() => {
//     if (!show) {
//       setAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);

//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//     }
//   }, [show]);

//   if (!show) return null;

//   // Берём нужные картинки (внутреннее колесо + наружное кольцо)
//   const wheelImage = wheelImagesMap[currentBuyIn] || defaultWheelImage;
//   const outerRingImage = outerRingImagesMap[currentBuyIn] || defaultOuterRingImage;

//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-container">
//         {/* Наружный круг по текущему байину */}
//         <img
//           className="outer-ring"
//           src={outerRingImage}
//           alt="Outer ring"
//         />
        
//         {/* Основная часть колеса (по текущему байину) */}
//         <img
//           className="inner-circle"
//           src={wheelImage}
//           alt="Inner circle"
//           style={{
//             transform: `rotate(${angle}deg)`,
//             transition: spinning ? 'transform 5s ease-out' : 'none',
//           }}
//         />
//       </div>

//       {/* Призовое сообщение */}
//       {finalPrize && (
//         <div className="prize-message">
//           Вам выпал приз: <strong>{finalPrize}</strong>
//         </div>
//       )}
//     </div>
//   );
// }














// import React, { useState, useEffect, useRef } from 'react';
// import tournamentSocket from './tournamentSocket';
// import './SpinWheelOverlay.css';

// // Словарь: buyIn -> путь к картинке
// // (пример, поменяйте пути под ваши реальные файлы)
// const wheelImagesMap = {
//   1:  '/img/Wheel.png',
//   2:  '/img/5556.jpg',
//   5:  '/img/wheelForBuyIn5.png',
//   10: '/img/wheelForBuyIn10.png',
//   20: '/img/wheelForBuyIn20.png',
//   30: '/img/wheelForBuyIn30.png',
//   50: '/img/Koleso50.png',
// };

// // Если на какой-то buyIn нет картинки, подставим дефолт
// const defaultWheelImage = '/img/wheelDefault.png';

// // Если указатель (стрелка) у вас «на 9 часов» — используем offset=180
// const OFFSET = 180;

// export default function SpinWheelOverlay() {
//   const [show, setShow] = useState(false);
//   const [angle, setAngle] = useState(0);
//   const [spinning, setSpinning] = useState(false);
//   const [finalPrize, setFinalPrize] = useState(null);

//   // Запоминаем, для какого buyIn сейчас крутим колесо:
//   const [currentBuyIn, setCurrentBuyIn] = useState(1);

//   // Таймер, который скроет оверлей через N секунд
//   const hideTimerRef = useRef(null);

//   useEffect(() => {
//     // Функция-обработчик события "spinWheel", которое шлёт сервер
//     function handleSpinWheel({ angle, prize, buyIn, message }) {
//       console.log('[SpinWheelOverlay] => spinWheel:', { angle, prize, buyIn, message });

//       // Показываем оверлей
//       setShow(true);
//       // Сбрасываем старые значения
//       setAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);

//       // Запоминаем buyIn, чтобы знать, какую картинку показывать
//       setCurrentBuyIn(buyIn || 1);

//       // Запускаем анимацию колеса через небольшую паузу
//       setTimeout(() => {
//         setAngle(angle);   // угол уже готов (пришёл с сервера)
//         setSpinning(true);
//       }, 100);

//       // Запускаем таймер скрытия через 12с (пример)
//       if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
//       hideTimerRef.current = setTimeout(() => {
//         setShow(false);
//       }, 12000);

//       // Запоминаем приз
//       setFinalPrize(prize);
//     }

//     // Подписка на событие от сервера
//     tournamentSocket.on('spinWheel', handleSpinWheel);

//     // Отписка при размонтировании
//     return () => {
//       tournamentSocket.off('spinWheel', handleSpinWheel);
//     };
//   }, []);

//   // Когда оверлей скрывается — сбрасываем
//   useEffect(() => {
//     if (!show) {
//       setAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);

//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//     }
//   }, [show]);

//   // Если скрыто — ничего не рендерим
//   if (!show) return null;

//   // Берём картинку по buyIn (или дефолт)
//   const wheelImage = wheelImagesMap[currentBuyIn] || defaultWheelImage;

//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-container">
//         {/* Наружный круг (если нужен одинаковый для всех) */}
//         <img className="outer-ring" src="/img/NarujKrug.png" alt="Outer ring" />
        
//         {/* Основная часть колеса — своё изображение под каждый buyIn */}
//         <img
//           className="inner-circle"
//           src={wheelImage}
//           alt="Inner circle"
//           style={{
//             transform: `rotate(${angle}deg)`,
//             transition: spinning ? 'transform 5s ease-out' : 'none',
//           }}
//         />
//       </div>

//       {/* Отображаем приз текстом */}
//       {finalPrize && (
//         <div className="prize-message">
//           Вам выпал приз: <strong>{finalPrize}</strong>
//         </div>
//       )}
//     </div>
//   );
// }









// import React, { useState, useEffect, useRef } from 'react';
// import tournamentSocket from './tournamentSocket';
// import './SpinWheelOverlay.css';

// // Стрелка находится «слева», поэтому добавляем оффсет на 180 градусов
// const offset = 180;

// // Для удобства – каждому призу соответствующий «центральный» угол.
// // Здесь: сектор 1 → 0°, сектор 2 → 30°, ..., сектор 12 → 330°.
// const sectorAngles = {
//   '3': 0,
//   '50': 30,
//   '5': 60,
//   '15': 90,
//   '4': 120,
//   '20': 150,
//   '8': 180,
//   '100': 210,
//   '2': 240, 
//   '10': 270,
//   '12': 300,
//   '30': 330,
// };

// export default function SpinWheelOverlay() {
//   const [show, setShow] = useState(false);
//   const [angle, setAngle] = useState(0);
//   const [spinning, setSpinning] = useState(false);
//   const [finalPrize, setFinalPrize] = useState(null);
//   const hideTimerRef = useRef(null);

//   useEffect(() => {
//     function handleSpinWheel({ prize }) {
//       console.log('Приз:', prize);
//       setShow(true);
//       setFinalPrize(null);
//       setSpinning(false);
//       setAngle(0);

//       // Находим «базовый» угол нужного сектора
//       const baseAngle = sectorAngles[prize] ?? 0;

//       // Рандомный сдвиг, чтобы не было остановки строго по центру сектора
//       const randomShift = Math.floor(Math.random() * 11) - 5; // от -5 до +5°
//       const sectorAngle = baseAngle + randomShift;

//       // Добавляем, например, 4 полных оборота = 1440°, 
//       // и смещаем на нужный сектор минус offset (у нас стрелка на 9 часов)
//       const totalAngle = 1440 + sectorAngle - offset;

//       // Через небольшую паузу запускаем анимацию
//       setTimeout(() => {
//         setAngle(totalAngle);
//         setSpinning(true);
//       }, 100);

//       // Прячем оверлей после 12 секунд (к примеру)
//       if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
//       hideTimerRef.current = setTimeout(() => {
//         setShow(false);
//       }, 12000);

//       setFinalPrize(prize);
//     }

//     tournamentSocket.on('spinWheel', handleSpinWheel);
//     return () => {
//       tournamentSocket.off('spinWheel', handleSpinWheel);
//     };
//   }, []);

//   useEffect(() => {
//     if (!show) {
//       setSpinning(false);
//       setAngle(0);
//       setFinalPrize(null);
//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//     }
//   }, [show]);

//   if (!show) return null;

//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-container">
//         <img className="outer-ring" src="/img/NarujKrug.png" alt="Outer ring" />
//         <img
//           className="inner-circle"
//           src="/img/CentrKrug.png"
//           alt="Inner circle"
//           style={{
//             transform: `rotate(${angle}deg)`,
//             transition: spinning ? 'transform 5s ease-out' : 'none',
//           }}
//         />
//       </div>
//       {finalPrize && (
//         <div className="prize-message">
//           Вам выпал приз: <strong>{finalPrize}</strong>
//         </div>
//       )}
//     </div>
//   );
// }










// import React, { useState, useEffect, useRef } from 'react';
// import tournamentSocket from './tournamentSocket'; // Ваш Socket.IO клиент
// import './SpinWheelOverlay.css';

// export default function SpinWheelOverlay() {
//   const [show, setShow] = useState(false);          
//   const [angle, setAngle] = useState(0);            
//   const [spinning, setSpinning] = useState(false);  
//   const [finalPrize, setFinalPrize] = useState(null);

//   // Таймер для автозакрытия
//   const hideTimerRef = useRef(null);

//   useEffect(() => {
//     function handleSpinWheel({ angle: serverAngle, prize, message }) {
//       console.log('[CLIENT] spinWheel:', { serverAngle, prize, message });
      
//       // 1) Показываем колесо
//       setShow(true);

//       // 2) Сбрасываем старое
//       setFinalPrize(null);
//       setSpinning(false);
//       setAngle(0);

//       /**
//        * 3) Корректируем угол (offset), если:
//        *    - "0°" на сервере = сектор приз "вверху" (12 часов)
//        *    - У вас стрелка = 9 часов
//        *    - Сам сектор "100$" (или любой другой) на колесе
//        *      физически нарисован не совпадая с "12ч".
//        *
//        *   Подбираем offset = 120, 90, 180... опытным путём!
//        */
//       const offset = 120;  // Подберите вручную

//       // Увеличим число оборотов, чтобы колесо крутилось дольше
//       // Например, 1440° = 4 полных оборота.
//       // Можете поменять на 1080 (3 оборота) или 720 (2 оборота).
//       const totalAngle = 1440 + (serverAngle - offset);

//       // 4) Ставим небольшой таймер (100 мс),
//       //    чтобы колесо сначала «отрендерилось» в нулевом положении:
//       setTimeout(() => {
//         setAngle(totalAngle);
//         setSpinning(true);
//         setFinalPrize(prize);
//       }, 100);

//       // 5) Автозакрытие через 12 секунд (5 сек вращение + запас)
//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//       }
//       hideTimerRef.current = setTimeout(() => {
//         setShow(false);
//       }, 12000);
//     }

//     // Подписка на событие
//     tournamentSocket.on('spinWheel', handleSpinWheel);

//     // Чистим подписку при размонтировании
//     return () => {
//       tournamentSocket.off('spinWheel', handleSpinWheel);
//     };
//   }, []);

//   // Когда скрываем — сбрасываем
//   useEffect(() => {
//     if (!show) {
//       setSpinning(false);
//       setAngle(0);
//       setFinalPrize(null);

//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//     }
//   }, [show]);

//   // Если не показываем — ничего не рендерим
//   if (!show) return null;

//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-container">
//         {/* Внешний круг (со стрелкой слева на картинке) */}
//         <img
//           className="outer-ring"
//           src="/img/NarujKrug.png"
//           alt="Outer ring"
//         />

//         {/* Внутренний круг (вращается) */}
//         <img
//           className="inner-circle"
//           src="/img/CentrKrug.png"
//           alt="Inner circle"
//           style={{
//             transform: `rotate(${angle}deg)`,
//             // 5 секунд вращения + плавное замедление
//             transition: spinning
//               ? 'transform 5s ease-out'
//               : 'none',
//           }}
//         />
//       </div>

//       {/* Показать приз, если он есть */}
//       {finalPrize && (
//         <div className="prize-message">
//           Вам выпал приз: <strong>{finalPrize}</strong>
//         </div>
//       )}
//     </div>
//   );
// }


// import React, { useState, useEffect, useRef } from 'react';
// import tournamentSocket from './tournamentSocket'; // <-- ВАШ Socket.IO клиент
// import './SpinWheelOverlay.css';

// export default function SpinWheelOverlay() {
//   const [show, setShow] = useState(false);           // Показать или скрыть колесо
//   const [angle, setAngle] = useState(0);            // Угол вращения
//   const [spinning, setSpinning] = useState(false);
//   const [finalPrize, setFinalPrize] = useState(null);

//   const hideTimerRef = useRef(null);

//   // ====== 1) Подписка на событие "spinWheel" ======
//   useEffect(() => {
//     // Когда сервер шлёт "spinWheel", мы принимаем (angle, prize, message)
//     function handleSpinWheel({ angle, prize, message }) {
//       console.log('[CLIENT] Получено spinWheel:', { angle, prize, message });
//       // 1) Показываем колесо
//       setShow(true);
//       // 2) Ставим угол
//       setAngle(angle);
//       // 3) Сбрасываем прошлый finalPrize
//       setFinalPrize(null);
//     }

//     // Подписываемся
//     tournamentSocket.on('spinWheel', handleSpinWheel);

//     // Чистка при размонтировании
//     return () => {
//       tournamentSocket.off('spinWheel', handleSpinWheel);
//     };
//   }, []);

//   // ====== 2) Запускаем/сбрасываем анимацию, когда show/angle меняется ======
//   useEffect(() => {
//     if (!show) {
//       // Скрываем колесо => сбрасываем стейт
//       setSpinning(false);
//       setAngle(0);
//       setFinalPrize(null);

//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//       return;
//     }

//     // Иначе (show=true) => крутим колесо
//     setSpinning(true);
//     // Ставим угол сразу: (у вас было через setTimeout, но можно и без)
//     // Если хотите плавный старт, оставьте таймер:
//     // const tId = setTimeout(() => setAngle(angle), 60);
//     // return () => clearTimeout(tId);

//   }, [show, angle]);

//   // ====== 3) По завершении анимации ======
//   const handleTransitionEnd = () => {
//     setSpinning(false);

//     // Здесь мы можем показать реальный приз
//     // (т.к. у нас уже есть finalPrize в стейте? 
//     //  Мы до сих пор его не ставили... но можно!)
//     //  * Но мы получили приз в handleSpinWheel => 
//     //    можем сохранять его в finalPrize:
//     //   setFinalPrize(prize); // Но приз? 
//     //   Для этого нужно где-то хранить prize, 
//     //   например setLocalPrize(prize)...

//     // Для упрощения предположим, что "finalPrize" == prize,
//     // тогда в handleSpinWheel => setFinalPrize(prize).
//     // Или поставим тут вручную:
//     // setFinalPrize('Приз #' + angle); // временно

//     hideTimerRef.current = setTimeout(() => {
//       setShow(false); // через 5с скрываем колесо
//     }, 5000);
//   };

//   // Если show=false, колесо не отображаем
//   if (!show) {
//     return null;
//   }

//   return show ? (
//     <div className="wheel-overlay">
//       <div className="wheel-container">
//         {/* Внешний круг */}
//         <img
//           className="outer-ring"
//           src="/img/NarujKrug.png"
//           alt="Outer ring"
//         />
  
//         {/* Указатель */}
//         <div className="pointer" />
  
//         {/* Внутренний круг (вращается) */}
//         <img
//           className="inner-circle"
//           src="/img/CentrKrug.png"
//           alt="Inner circle"
//           style={{
//             transform: `rotate(${angle}deg)`,
//             transition: spinning
//               ? 'transform 4s cubic-bezier(0.33, 1, 0.68, 1)'
//               : 'none',
//           }}
//           onTransitionEnd={handleTransitionEnd}
//         />
//       </div>
  
//       {finalPrize && (
//         <div className="prize-message">
//           Выпал приз: <b>{finalPrize}</b>
//         </div>
//       )}
  
//       <button className="close-wheel" onClick={() => setShow(false)}>
//         Закрыть
//       </button>
//     </div>
//   ) : null;
// }





// import React, { useState, useEffect, useRef } from 'react';
// import './SpinWheelOverlay.css';

// export default function SpinWheelOverlay({
//   show,
//   angle,
//   onHide,
// }) {
//   const [currentAngle, setCurrentAngle] = useState(0);
//   const [spinning, setSpinning] = useState(false);
//   const [finalPrize, setFinalPrize] = useState(null);
  
//   const hideTimerRef = useRef(null);

//   useEffect(() => {
//     if (!show) {
//       setCurrentAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);
//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//       return;
//     }

//     setSpinning(true);
//     setFinalPrize(null);
//     setCurrentAngle(0);

//     const tId = setTimeout(() => {
//       setCurrentAngle(angle);
//     }, 60);

//     return () => {
//       clearTimeout(tId);
//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//     };
//   }, [show, angle]);

//   const handleTransitionEnd = () => {
//     setSpinning(false);
//     // В реальном проекте prize к нам пришёл **с сервера** — 
//     // можно передать пропом если нужно, 
//     // или получаем из другого стейта.
//     setFinalPrize('Показать реальный приз?');
    
//     hideTimerRef.current = setTimeout(() => {
//       if (onHide) onHide();
//     }, 5000);
//   };

//   if (!show) return null;

//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-container">
//         {/* Внешний круг */}
//         <img
//           className="outer-ring"
//           src="/img/NarujKrug.png"
//           alt="Outer ring"
//         />

//         {/* Указатель (сверху или слева — как верстка настроена) */}
//         <div className="pointer" />

//         {/* Внутренний круг (крутится) */}
//         <img
//           className="inner-circle"
//           src="/img/CentrKrug.png"
//           alt="Inner circle"
//           style={{
//             transform: `rotate(${currentAngle}deg)`,
//             transition: spinning
//               ? 'transform 4s cubic-bezier(0.33, 1, 0.68, 1)'
//               : 'none',
//           }}
//           onTransitionEnd={handleTransitionEnd}
//         />
//       </div>

//       {finalPrize && (
//         <div className="prize-message">Выпал приз: {finalPrize}</div>
//       )}

//       <button className="close-wheel" onClick={onHide}>Закрыть</button>
//     </div>
//   );
// }










// import React, { useState, useEffect, useRef } from 'react';
// import './SpinWheelOverlay.css';

// export default function SpinWheelOverlay({
//   show,
//   angle,
//   prizes = [],
//   onHide,
// }) {
//   const [currentAngle, setCurrentAngle] = useState(0);
//   const [spinning, setSpinning] = useState(false);
//   const [finalPrize, setFinalPrize] = useState(null);
  
//   const hideTimerRef = useRef(null);

//   // Запускаем/сбрасываем анимацию при изменении show
//   useEffect(() => {
//     if (!show) {
//       setCurrentAngle(0);
//       setSpinning(false);
//       setFinalPrize(null);
//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//       return;
//     }

//     // Запуск вращения
//     setSpinning(true);
//     setFinalPrize(null);
//     setCurrentAngle(0); // сброс

//     const tid = setTimeout(() => {
//       setCurrentAngle(angle);
//     }, 60);

//     return () => {
//       clearTimeout(tid);
//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//     };
//   }, [show, angle]);

//   // ... (логика расчёта призового сектора, handleTransitionEnd и т.п.)
//   const handleTransitionEnd = () => {
//     setSpinning(false);
//     // Ваш код определения, какой приз выпал
//     setFinalPrize('???');
//     // Автозакрытие
//     hideTimerRef.current = setTimeout(() => {
//       if (onHide) onHide();
//     }, 5000);
//   };

//   if (!show) return null;

//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-container">
//         {/* Внешний круг */}
//         <img
//           className="outer-ring"
//           src="/img/NarujKrug.png"
//           alt="Outer ring"
//         />

//         {/* Указатель (треугольник наверху) */}
//         <div className="pointer" />

//         {/* Внутренний круг, вращается */}
//         <img
//           className="inner-circle"
//           src="/img/CentrKrug.png"
//           alt="Inner circle"
//           style={{
//             transform: `rotate(${currentAngle}deg)`,
//             transition: spinning
//               ? 'transform 4s cubic-bezier(0.33, 1, 0.68, 1)'
//               : 'none',
//           }}
//           onTransitionEnd={handleTransitionEnd}
//         />
//       </div>

//       {finalPrize && (
//         <div className="prize-message">
//           Выпал приз: <b>{finalPrize}</b>
//         </div>
//       )}

//       <button className="close-wheel" onClick={onHide}>
//         Закрыть
//       </button>
//     </div>
//   );
// }














// import React, { useState, useEffect, useRef } from 'react';
// import './SpinWheelOverlay.css';

// /**
//  * SpinWheelOverlay
//  * props:
//  *  - show: boolean, показывать ли колесо
//  *  - angle: число, конечный угол (например, 720 + randomDeg)
//  *  - prizes: массив призов (числа)
//  *  - onHide: колбэк, который скроет колесо
//  */
// export default function SpinWheelOverlay({
//   show,
//   angle,
//   prizes = [],
//   onHide,
// }) {
//   // ------ Состояния ------
//   const [currentAngle, setCurrentAngle] = useState(0);
//   const [spinning, setSpinning] = useState(false);
//   const [winningIndex, setWinningIndex] = useState(null);
//   const [finalPrize, setFinalPrize] = useState(null);

//   // Таймер автозакрытия
//   const hideTimerRef = useRef(null);

//   // Запускаем/сбрасываем анимацию при изменении show
//   useEffect(() => {
//     if (!show) {
//       // Если колесо прятать — сбрасываем всё и чистим таймер
//       setCurrentAngle(0);
//       setSpinning(false);
//       setWinningIndex(null);
//       setFinalPrize(null);

//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//       return;
//     }

//     // Иначе (show=true), запускаем вращение
//     setSpinning(true);
//     setWinningIndex(null);
//     setFinalPrize(null);
//     setCurrentAngle(0); // сброс угла, чтобы CSS-транзишн начался

//     // Небольшая задержка (60 мс), чтобы анимация «плавно» сработала
//     const tid = setTimeout(() => {
//       setCurrentAngle(angle);
//     }, 60);

//     return () => {
//       clearTimeout(tid);
//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//     };
//   }, [show, angle]);

//   // Если призов нет, флаг:
//   const noPrizes = !prizes || prizes.length === 0;

//   // Для вычисления сектора используем «веса» (1 / prize),
//   // как и в вашем исходном примере (если нужно):
//   const weights = noPrizes ? [] : prizes.map((p) => 1 / p);
//   const totalWeight = weights.reduce((acc, w) => acc + w, 0);

//   // Каждый сектор = (w_i / totalWeight)*360
//   const angleArr = weights.map((w) => (w / totalWeight) * 360);

//   // Формируем «сектора»
//   let start = 0;
//   const sectors = angleArr.map((ang, i) => {
//     const s = {
//       index: i,
//       prize: prizes[i],
//       startAngle: start,
//       endAngle: start + ang,
//     };
//     start += ang;
//     return s;
//   });

//   // -------------------------------------------
//   // Когда заканчивается анимация (transitionEnd)
//   // -------------------------------------------
//   const handleTransitionEnd = () => {
//     setSpinning(false);

//     // Текущий угол колеса (в диапазоне [0..360))
//     let deg = currentAngle % 360;
//     if (deg < 0) deg += 360;

//     // Так как указатель теперь слева (9 часов), 
//     // «0°» для вычисления нам надо сдвинуть на 90°:
//     const pointerDeg = (deg + 90) % 360;

//     // Ищем, в каком секторе лежит pointerDeg
//     const found = sectors.find(
//       (s) => pointerDeg >= s.startAngle && pointerDeg < s.endAngle
//     );

//     if (found) {
//       setWinningIndex(found.index);
//       setFinalPrize(found.prize);
//       console.log(`[Wheel] Остановились: угол=${deg}, prize=${found.prize}`);
//     } else if (sectors.length > 0) {
//       // fallback
//       const last = sectors[sectors.length - 1];
//       setWinningIndex(last.index);
//       setFinalPrize(last.prize);
//     }

//     // Запускаем автоскрытие через 5 секунд
//     hideTimerRef.current = setTimeout(() => {
//       if (onHide) onHide();
//     }, 5000);
//   };

//   // Ручное закрытие
//   const handleClose = () => {
//     if (hideTimerRef.current) {
//       clearTimeout(hideTimerRef.current);
//       hideTimerRef.current = null;
//     }
//     if (onHide) onHide();
//   };

//   // ----- Условный рендер -----
//   if (!show) return null;

//   if (noPrizes) {
//     return (
//       <div className="wheel-overlay">
//         <div className="wheel-container">
//           <div style={{ padding: '1rem', color: '#fff' }}>
//             Нет призов для отображения
//           </div>
//         </div>
//         <button className="close-wheel" onClick={handleClose}>
//           Закрыть
//         </button>
//       </div>
//     );
//   }

//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-container">

//         {/* Внешний круг (PNG или JPG), статичное изображение */}
//         <img
//           className="outer-ring"
//           src="./img/NarujKrug.png"
//           alt="Outer ring"
//         />

//         {/* Указатель слева (на 9 часов).
//             Можно сделать картинкой, а можно просто треугольником через border.
//             Для примера делаем div, стили в CSS. */}
//         <div className="pointer" />

//         {/* Внутренний круг, именно он вращается */}
//         <img
//           className="inner-circle"
//           src="./img/CentrKrug.png"
//           alt="Inner circle"
//           style={{
//             transform: `rotate(${currentAngle}deg)`,
//             transition: spinning
//               ? 'transform 4s cubic-bezier(0.33, 1, 0.68, 1)'
//               : 'none',
//           }}
//           onTransitionEnd={handleTransitionEnd}
//         />

//       </div>

//       {/* Сообщение о призе, если уже определён */}
//       {finalPrize !== null && (
//         <div className="prize-message">
//           Выпал приз: <b>{finalPrize}</b>
//         </div>
//       )}

//       <button className="close-wheel" onClick={handleClose}>
//         Закрыть
//       </button>
//     </div>
//   );
// }














// import React, { useState, useEffect, useRef } from 'react';
// import './SpinWheelOverlay.css';

// /**
//  * SpinWheelOverlay
//  * props:
//  *  - show: boolean, показывать ли колесо
//  *  - angle: число, конечный угол (например, 720 + randomDeg)
//  *  - prizes: массив призов (числа)
//  *  - onHide: колбэк, который скроет колесо
//  */
// export default function SpinWheelOverlay({
//   show,
//   angle,
//   prizes = [],
//   onHide,
// }) {
//   // ------ Состояния ------
//   const [currentAngle, setCurrentAngle] = useState(0);
//   const [spinning, setSpinning] = useState(false);
//   const [winningIndex, setWinningIndex] = useState(null);
//   const [finalPrize, setFinalPrize] = useState(null);

//   // Ссылка на таймер, чтобы можно было сбросить
//   const hideTimerRef = useRef(null);

//   // ------ Хук useEffect (всегда вызывается) ------
//   useEffect(() => {
//     // При каждом рендере компонент мы проверяем show.
//     // Если show=false => сбрасываем состояния, останавливаем таймер.
//     if (!show) {
//       setCurrentAngle(0);
//       setSpinning(false);
//       setWinningIndex(null);
//       setFinalPrize(null);

//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//       return; // <-- Здесь "возвращаемся" только из эффекта, а не из компонента.
//     }

//     // Если show=true => "запуск" анимации
//     setSpinning(true);
//     setWinningIndex(null);
//     setFinalPrize(null);
//     setCurrentAngle(0); // Сначала сброс

//     // А затем через короткую паузу (60 мс) ставим угол (angle),
//     // чтобы CSS-transition сработал плавно.
//     const tid = setTimeout(() => {
//       setCurrentAngle(angle);
//     }, 60);

//     // Очистка на размонтировании/смене show
//     return () => {
//       clearTimeout(tid);
//       if (hideTimerRef.current) {
//         clearTimeout(hideTimerRef.current);
//         hideTimerRef.current = null;
//       }
//     };
//   }, [show, angle]);

//   // ------ Построение секторов по призам ------

//   // Если массив призов пуст, сделаем флаг:
//   const noPrizes = !prizes || prizes.length === 0;

//   // Для удобства ниже в коде
//   // (Если хочется что-то отобразить, когда нет призов, можно):
//   // if (noPrizes) {
//   //   // Ничего не делаем, но компонент не рендерим колесо
//   // }

//   // Построение "весов": чем больше приз, тем меньше сектор
//   // w_i = 1 / prize_i
//   const weights = noPrizes
//     ? []
//     : prizes.map((p) => 1 / p);

//   const totalWeight = weights.reduce((acc, w) => acc + w, 0);

//   // Каждый сектор = (w_i / totalWeight)*360 градусов
//   const angleArr = weights.map((w) => (w / totalWeight) * 360);

//   // Накопленная сумма, чтобы знать startAngle..endAngle
//   let start = 0;
//   const sectors = angleArr.map((ang, i) => {
//     const s = {
//       index: i,
//       prize: prizes[i],
//       startAngle: start,
//       endAngle: start + ang,
//     };
//     start += ang;
//     return s;
//   });

//   // Цвета для градиента
//   const colors = [
//     '#fa3', '#3af', '#f3a', '#6c6', '#fc3', '#39f',
//     '#f63', '#c6f', '#6ff', '#ccc', '#eee',
//   ];

//   // Собираем части для conic-gradient
//   const gradientParts = [];
//   sectors.forEach((sec, i) => {
//     const color = colors[i % colors.length];
//     gradientParts.push(
//       `${color} ${sec.startAngle}deg ${sec.endAngle}deg`
//     );
//   });

//   // from -90deg => 0deg будет "сверху"
//   const gradientStr = `conic-gradient(from -90deg, ${gradientParts.join(', ')})`;

//   // Метки на колесе (центр сектора)
//   const labels = sectors.map((sec) => {
//     const mid = (sec.startAngle + sec.endAngle) / 2;
//     const isHighlight = sec.index === winningIndex;
//     const hlClass = isHighlight ? ' highlight' : '';

//     return (
//       <div
//         key={sec.index}
//         className={`sector-label${hlClass}`}
//         style={{
//           transform: `rotate(${mid}deg) translate(140px) rotate(-${mid}deg)`,
//         }}
//       >
//         {sec.prize}
//       </div>
//     );
//   });

//   // ------ Обработчик окончания анимации (transitionEnd) ------
//   const handleTransitionEnd = () => {
//     setSpinning(false);

//     // deg = [0..360)
//     let deg = currentAngle % 360;
//     if (deg < 0) deg += 360;

//     // Ищем сектор, куда попали
//     const found = sectors.find(
//       (s) => deg >= s.startAngle && deg < s.endAngle
//     );
//     if (found) {
//       setWinningIndex(found.index);
//       setFinalPrize(found.prize);
//       console.log(`[Wheel] Остановились на угол=${deg}, приз=${found.prize}`);
//     } else if (sectors.length > 0) {
//       // fallback: если не нашли, берём последний
//       const last = sectors[sectors.length - 1];
//       setWinningIndex(last.index);
//       setFinalPrize(last.prize);
//     }

//     // Запускаем автоскрытие через 5 секунд
//     hideTimerRef.current = setTimeout(() => {
//       if (onHide) onHide();
//     }, 5000);
//   };

//   // ------ Ручное закрытие ------
//   const handleClose = () => {
//     if (hideTimerRef.current) {
//       clearTimeout(hideTimerRef.current);
//       hideTimerRef.current = null;
//     }
//     if (onHide) onHide();
//   };

//   // ------ Условный рендер основного вывода ------
//   // 1) Если компонент "скрыт" (show=false), ничего не отображаем
//   //    (НО useEffect уже вызвали на верхнем уровне!)
//   if (!show) {
//     return null;
//   }

//   // 2) Если нет призов, можно отобразить какую-то заглушку
//   if (noPrizes) {
//     return (
//       <div className="wheel-overlay">
//         <div className="wheel-container">
//           <div style={{ padding: '1rem', color: '#fff' }}>
//             Нет призов для отображения
//           </div>
//         </div>
//         <button className="close-wheel" onClick={handleClose}>
//           Закрыть
//         </button>
//       </div>
//     );
//   }

//   // 3) Иначе показываем само колесо
//   return (
//     <div className="wheel-overlay">
//       <div className="wheel-container">
//         {/* Стрелка (сверху) */}
//         <div className="pointer" />

//         {/* Сам диск */}
//         <div
//           className="wheel-disk"
//           style={{
//             background: gradientStr,
//             transform: `rotate(${currentAngle}deg)`,
//             transition: spinning
//               ? 'transform 4s cubic-bezier(0.33, 1, 0.68, 1)'
//               : 'none',
//           }}
//           onTransitionEnd={handleTransitionEnd}
//         >
//           {labels}
//         </div>
//       </div>

//       {finalPrize !== null && (
//         <div className="prize-message">
//           Выпал приз: <b>{finalPrize}</b>
//         </div>
//       )}

//       <button className="close-wheel" onClick={handleClose}>
//         Закрыть
//       </button>
//     </div>
//   );
// }
